import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { reference, type DocsOperation, type SearchEntry } from '../generated/reference';
import searchIndex from '../generated/search-index.json';

export interface GuideResult {
  entry: SearchEntry;
  /** A heading inside a guide rather than the guide itself. */
  section: boolean;
}

export interface OperationResult {
  entry: SearchEntry;
  operation: DocsOperation;
}

const entries = searchIndex as SearchEntry[];

/** Built once: the index and the reference are static build output. */
function toResults() {
  const guides: GuideResult[] = [];
  const operations: OperationResult[] = [];
  for (const entry of entries) {
    if (entry.kind === 'guide') {
      guides.push({ entry, section: entry.id.includes('#') });
      continue;
    }
    const operation = reference.operations[entry.id.replace(/^operation:/, '')];
    if (operation) operations.push({ entry, operation });
  }
  return { guides, operations };
}

const results = toResults();

/**
 * The ⌘K / Ctrl+K palette over the build-time search index: guides with their h2/h3 sections,
 * then every operation. Filtering is cmdk's; this hook owns the open state, the shortcut and
 * navigation, which goes through the router so `#section` links land on the heading.
 */
export function useDocsSearch() {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = useCallback(
    (href: string) => {
      setOpen(false);
      void router.navigate({ href });
    },
    [router],
  );

  return { open, setOpen, go, ...results };
}
